import { useParams } from "react-router-dom";
import { useChangeAddQuestionChapter, useChangeExamStatus, useEditExamInfo, useExamChapterList, useAssignChaptersByAIRag, useExamDetail, useExamQuestionGet, useExamQuestionStatusGet, useSchoolChangeAddQuestion, useSharedNotes, useCreateSharedNote, useAssignSharedNote, useUpdateSharedNote, useDeleteExamQuestion, useGrades } from "../../react-query/queries";
import { useAuth } from "../../utils/Auth/AuthProvider";
import { FaEdit } from "react-icons/fa";
import { useEffect, useState, useRef } from "react";
import DOMPurify from 'dompurify';
import RichSlateEditor from './RichSlateEditor'
import katex from 'katex'
import 'katex/dist/katex.min.css'
import renderMathInElement from 'katex/contrib/auto-render'
import { FaCheck, FaTimes, FaInfoCircle, FaQuestion, FaChartLine, FaPlus } from "react-icons/fa";
import { Badge, Spinner, Button, Label, Select, Modal, Tabs, } from "flowbite-react";
import { IQuestion } from "../../types/question";
import { Editor as TMC } from "@tinymce/tinymce-react";
import ReactQuill from 'react-quill';
import Latex from 'react-latex-next'
import LaTeXBuilder from '../mathsymbols/LaTeXBuilder'
import AddOtherQuestionsDialog from "./AddOtherQuestionsDialog";


const ExamDetailComponent = () => {
    const { token, role } = useAuth();
    const { examId } = useParams();
    const [questionsArray, setQuestionsArray] = useState<any[]>([]);
    const [statusSwitch, setStatusSwitch] = useState(false);
    const [chapterEdit, setChapterEdit] = useState(false);
    const [questionChapterChanged, setquestionChapterChanged] = useState<{ [code: number]: string; }>({});
    const [openModal, setOpenModal] = useState(false);
    const [openVerificationModal, setOpenVerificationModal] = useState(false);
    const [chapterGradeModalOpen, setChapterGradeModalOpen] = useState(false);
    const [selectedGradeIds, setSelectedGradeIds] = useState<number[]>([]);
    const [appliedGradeIds, setAppliedGradeIds] = useState<number[]>([]);
    const [editInfoOpen, setEditInfoOpen] = useState(false);
    const [editName, setEditName] = useState<string>('');
    const [editMinutes, setEditMinutes] = useState<string>('');
    const [selectedQuestion, setSelectedQuestion] = useState<{
        ques: string,
        choice_a: string,
        choice_b: string,
        choice_c: string,
        choice_d: string,
        ans: string,
        id?: number | string,
        explanation?: string,
        reference?: string,
    }>({
        ques: '',
        choice_a: '',
        choice_b: '',
        choice_c: '',
        choice_d: '',
        ans: '',
    })

    const [selectedOption, setSelectedOption] = useState<string>('');
    const [isOptionSelected, setIsOptionSelected] = useState<boolean>(false);
    const [selectedIndex, setSelectedIndex] = useState<number>()
    const [showMathBuilder, setShowMathBuilder] = useState(false)

    const containerRef = useRef<HTMLDivElement | null>(null)

    // Shared note state
    const [selectedQuestionIds, setSelectedQuestionIds] = useState<number[]>([]);
    const [sharedNoteModalOpen, setSharedNoteModalOpen] = useState(false);
    const [newSharedNote, setNewSharedNote] = useState({ title: '', content: '' });
    const [existingSharedNoteId, setExistingSharedNoteId] = useState<string>('');

    const [addQuestionsOpen, setAddQuestionsOpen] = useState(false);

    const { data: sharedNotesData } = useSharedNotes();
    const createSharedNoteMut = useCreateSharedNote();
    const updateSharedNoteMut = useUpdateSharedNote();
    const assignSharedNoteMut = useAssignSharedNote(examId!);

    const [editSharedNoteModalOpen, setEditSharedNoteModalOpen] = useState(false);
    const [editingSharedNote, setEditingSharedNote] = useState<{ id: number; title: string; content: string } | null>(null);

    const openEditSharedNote = (note: any) => {
        // eslint-disable-next-line no-console
        console.log('openEditSharedNote raw note:', note);
        let contentValue = '';
        if (note.content != null) {
            if (typeof note.content === 'string') {
                contentValue = note.content;
            } else if (typeof note.content === 'object') {
                // Try common fields, then stringify if all else fails
                contentValue = note.content.content || note.content.html || note.content.text || JSON.stringify(note.content);
            } else {
                contentValue = String(note.content);
            }
        }
        
        // ensure valid HTML wrapping for RichSlateEditor if it looks like plain text
        if (contentValue && !contentValue.trim().startsWith('<')) {
            contentValue = `<p>${contentValue}</p>`;
        }

        setEditingSharedNote({ 
            id: note.id, 
            title: note.title || '', 
            content: contentValue
        });
        setEditSharedNoteModalOpen(true);
    };

    const handleUpdateSharedNote = async () => {
        if (editingSharedNote) {
            await updateSharedNoteMut.mutateAsync({
                id: editingSharedNote.id,
                title: editingSharedNote.title,
                content: editingSharedNote.content,
            });
            await refetchQuestions();
            setEditSharedNoteModalOpen(false);
            setEditingSharedNote(null);
        }
    };

    const toggleQuestionSelection = (id: number) => {
        setSelectedQuestionIds(prev =>
            prev.includes(id) ? prev.filter(qid => qid !== id) : [...prev, id]
        );
    };

    const handleAssignSharedNote = async () => {
        let noteId = Number(existingSharedNoteId);
        if (!noteId && newSharedNote.title && newSharedNote.content) {
            const created = await createSharedNoteMut.mutateAsync(newSharedNote);
            noteId = created.id;
        }

        if (noteId && selectedQuestionIds.length > 0) {
            await assignSharedNoteMut.mutateAsync({
                note_id: noteId,
                question_ids: selectedQuestionIds,
            });
            await refetchQuestions();
            setSharedNoteModalOpen(false);
            setSelectedQuestionIds([]);
            setNewSharedNote({ title: '', content: '' });
            setExistingSharedNoteId('');
        }
    };


    // Render LaTeX expressions inside an HTML string using KaTeX, then sanitize.
    const renderLatexInHtml = (html: string) => {
        if (!html) return ''
        try {
            let out = String(html)
            out = out.replace(/\$\$([\s\S]+?)\$\$/g, (_m, expr) => {
                try { return katex.renderToString(expr, { displayMode: true, throwOnError: false }) } catch (e) { return _m }
            })
            out = out.replace(/\$(?!\$)([^$]+?)\$/g, (_m, expr) => {
                try { return katex.renderToString(expr, { displayMode: false, throwOnError: false }) } catch (e) { return _m }
            })
            try { return DOMPurify.sanitize(out) } catch (e) { return out }
        } catch (e) {
            try { return DOMPurify.sanitize(String(html)) } catch (err) { return String(html) }
        }
    }

    // Chapter grade filter popup state
    const [gradeOptions, setGradeOptions] = useState<{ id: number; name: string }[]>([]);


    const getFilteredChapters = (): any[] => {
        const chapters: any[] = (chapterListData as any)?.chapters || [];
        
        // If we are using the AI API (appliedGradeIds has values), 
        // the backend already filtered by grades, so we return as is.
        if (appliedGradeIds && appliedGradeIds.length > 0) {
            return chapters;
        }

        // Legacy filtering for the full institution chapter list
        if (selectedGradeIds && selectedGradeIds.length > 0) {
            const filtered = chapters.filter(c => {
                // Support various backend field names for grade ID(s)
                const cgIds: any[] = [];
                if (Array.isArray(c.school_grade_ids)) cgIds.push(...c.school_grade_ids);
                if (Array.isArray(c.school_grades)) cgIds.push(...c.school_grades.map((g: any) => g.id || g));
                if (c.grade_id != null) cgIds.push(c.grade_id);

                return cgIds.some((cgId: any) => 
                    selectedGradeIds.some(sgId => String(sgId) === String(cgId))
                );
            });
            return filtered;
        }
        return chapters;
    }

    const { status: examStatus, data: examData, error: examError, isFetching: examIsFetching } = useExamDetail(token, examId)
    const { data: gradesData } = useGrades(token);
    const { status: statStatus, data: statData, error: statError, isFetching: isStatFetching } = useExamQuestionStatusGet(token, examId)
    const { status: questionsStatus, data: questionsData, error: questionsError, isFetching: questionIsFetching, refetch: refetchQuestions } = useExamQuestionGet(token, examId)
    
    // Switch between default and AI RAG chapters based on whether grades are applied
    const { status: chapterListStatusLegacy, data: chapterListDataLegacy, error: chapterListErrorLegacy, isFetching: chapterListIsFetchingLegacy } = useExamChapterList(token, examId, chapterEdit && appliedGradeIds.length === 0)
    const { status: chapterListStatusAI, data: chapterListDataAI, error: chapterListErrorAI, isFetching: chapterListIsFetchingAI } = useAssignChaptersByAIRag(token, examId, appliedGradeIds, chapterEdit && appliedGradeIds.length > 0)

    const chapterListData = appliedGradeIds.length > 0 ? chapterListDataAI : chapterListDataLegacy;
    const chapterListIsFetching = appliedGradeIds.length > 0 ? chapterListIsFetchingAI : chapterListIsFetchingLegacy;

    const { mutateAsync: schoolExamStatusChange, isPending: isExamStatusChanging } = useChangeExamStatus(examId!)
    const { mutateAsync: schoolQuestionChapterChange, isPending: isSchoolQuestionchapterChange } = useChangeAddQuestionChapter(examId!)
    const { mutateAsync: schoolExamQuestionChange, isPending: isExamQuestionChanging } = useSchoolChangeAddQuestion(examId!)
    const { mutateAsync: editExamInfoMut, isPending: isEditing } = useEditExamInfo(examId!)
    const deleteQuestionMut = useDeleteExamQuestion(examId!);

    const subjectName: string | undefined = (examData as any)?.exam?.subject;

    useEffect(() => {
        if (chapterGradeModalOpen && gradesData?.grades) {
            const opts = gradesData.grades.map((g: any) => ({
                id: g.id,
                name: g.grade_name || g.name
            }));
            setGradeOptions(opts);
            // If nothing selected yet, default to all
            if (selectedGradeIds.length === 0) {
                setSelectedGradeIds(opts.map((o: any) => o.id));
            }
        }
    }, [chapterGradeModalOpen, gradesData]);

    const changingExamStatus = async (event: React.FormEvent) => {
        event.preventDefault();
        const next = !statusSwitch;
        await schoolExamStatusChange({ toBeValidated: token, exam_id: examId!, status: next ? 'Active' : 'Draft' }, {
            onSuccess(_data: any, variables: any, context: any) {
                // no-op
            },
        } as any);
        setStatusSwitch(next);
    }

    const questionEdit = (ind: number) => {
        // Use the same source array that we used when rendering the questions list
        // (some responses include a `full` array with richer objects)
        const sourceArr: any[] = (questionsData && (questionsData as any).full) ? (questionsData as any).full : (questionsData && questionsData.questions ? questionsData.questions : []);
        const q = sourceArr[ind]
        // dev: log incoming question shape to help debug object rendering
        // eslint-disable-next-line no-console
        console.log('questionEdit raw:', q)
        // helper: if value is a string and doesn't contain a block tag, wrap in <p>..</p>
        const wrapWithParagraph = (val: any) => {
            if (val == null) return ''
            // if it's already a string
            if (typeof val === 'string') {
                const s = val.trim()
                // look for common block-level tags at start
                if (/^<\/?(p|h1|h2|h3|h4|ul|ol|blockquote|div|li)\b/i.test(s)) return s
                // otherwise wrap
                return `<p>${s}</p>`
            }
            // if it's an object, try to find nested string fields
            try {
                if (typeof val === 'object') {
                    for (const key of ['ques', 'question_html', 'html', 'content', 'text', 'value']) {
                        if (typeof (val as any)[key] === 'string') {
                            return wrapWithParagraph((val as any)[key])
                        }
                    }
                }
            } catch (e) {
                // fallthrough
            }
            // fallback to string conversion wrapped
            return `<p>${String(val)}</p>`
        }

        // normalize fields to the editor shape and ensure wrapping
        const normalized = {
            id: q.id ?? q.question ?? undefined,
            ques: wrapWithParagraph(q.ques ?? q.question_html ?? ''),
            choice_a: wrapWithParagraph(q.option1 ?? q.choice_a ?? q.choiceA ?? ''),
            choice_b: wrapWithParagraph(q.option2 ?? q.choice_b ?? q.choiceB ?? ''),
            choice_c: wrapWithParagraph(q.option3 ?? q.choice_c ?? q.choiceC ?? ''),
            choice_d: wrapWithParagraph(q.option4 ?? q.choice_d ?? q.choiceD ?? ''),
            ans: q.ans ?? q.correct_ans ?? q.corrected_answer ?? '',
            // ensure explanation is valid HTML for the RichSlateEditor
            explanation: wrapWithParagraph(q.explanation ?? ''),
            // reference is plain text (may contain '||' separators)
            reference: q.reference != null ? String(q.reference) : ''
        }
    // eslint-disable-next-line no-console
    console.log('questionEdit normalized:', normalized)
        setSelectedQuestion(normalized as any)
        // setSelectedOption should be the raw answer value (e.g. 'A'|'B'|'C'|'D'),
        // not the HTML-wrapped version used for the editor's ans field.
        const rawAns = (q?.ans ?? q?.correct_ans ?? q?.corrected_answer ?? '').toString().trim()
        setSelectedOption(rawAns)
        setSelectedIndex(ind)
        setOpenModal(true)
    }

    const changingChpater = async (event: React.FormEvent) => {
        setOpenVerificationModal(false)
        event.preventDefault();

        schoolQuestionChapterChange({ toBeValidated: token, exam_id: examId!, data: questionChapterChanged }, {
            onSuccess(data, variables, context) {
                if (data['stat'] == 'done') {
                    setquestionChapterChanged({})
                }
            },
        })


    }

    const changeChapterOnUpdate = (chap: string, ques: number) => {
        setquestionChapterChanged(prevState => ({
            ...prevState,
            [ques]: chap
        }))
    }

    const changeSetChapterEdit = () => {
        const next = !chapterEdit;
        setChapterEdit(next)
        if (next) {
            setChapterGradeModalOpen(true);
        } else {
            setChapterGradeModalOpen(false);
        }
    }

    const toggleGrade = (id: number) => {
        setSelectedGradeIds(prev =>
            prev.includes(id) ? prev.filter(gid => gid !== id) : [...prev, id]
        );
    }

    const selectAllGrades = () => {
        setSelectedGradeIds(gradeOptions.map(o => o.id));
    }

    const clearAllGrades = () => {
        setSelectedGradeIds([]);
    }

    // Refresh questions when grades are applied to update the available chapters
    useEffect(() => {
        if (chapterEdit) {
            refetchQuestions();
        }
    }, [appliedGradeIds, chapterEdit, refetchQuestions]);

    const openEditInfo = () => {
        const nm = ((examData as any)?.exam?.name) ?? '';
        const tm = ((examData as any)?.exam?.time) ?? '';
        setEditName(nm as string);
        setEditMinutes(tm !== null && tm !== undefined ? String(tm) : '');
        setEditInfoOpen(true);
    }

    const saveEditInfo = async () => {
        const payload: any = {};
        if (editName && editName.trim().length > 0) payload.name = editName.trim();
        if (editMinutes !== '') {
            const n = Number(editMinutes);
            if (!Number.isNaN(n) && n >= 0) payload.time_minutes = n;
        }
        await editExamInfoMut({ toBeValidated: token, exam_id: examId!, payload });
        setEditInfoOpen(false);
    }

    const questionEditChange = (event: string, name: string) => {
        setSelectedQuestion((prevFormData) => ({
            ...prevFormData,
            [name]: event,
        }));
    };

    const saveQuestionEdit = async (event: React.FormEvent) => {
        setOpenModal(false)
        event.preventDefault();

        schoolExamQuestionChange({ toBeValidated: token, exam_id: examId!, data: selectedQuestion }, {
            onSuccess(data, variables, context) {
                console.log(data)
            },
        })
        setOpenModal(false)


    }

    const handleDeleteQuestion = async (questionId: number | string) => {
        if (window.confirm("Are you sure you want to delete this question?")) {
            await deleteQuestionMut.mutateAsync({ question_id: questionId });
        }
    }


    const changeTextColor = () => {
        setIsOptionSelected(true);
    };


    useEffect(() => {
        if (!examIsFetching && statData && typeof (statData as any).active === 'boolean') {
            setStatusSwitch(!!(statData as any).active);
        }
    }, [statData, examIsFetching]);

    const [operators, setOperators] = useState<any>([]);
  const [displayHistory, setDisplayHistory] = useState(false);
  const [
    displayDeleteButtonOnHistory,
    setDisplayDeleteButtonOnHistory
  ] = useState(false);

  const toggleDisplayHistory = (event: { target: { checked: boolean | ((prevState: boolean) => boolean); }; }) => {
    setDisplayHistory(event.target.checked);
  };

  const toggleDisplayDeleteOnHistory = (event: { target: { checked: boolean | ((prevState: boolean) => boolean); }; }) => {
    setDisplayDeleteButtonOnHistory(event.target.checked);
  };
  const CUSTOM_OPERATORS = [
    ["\\pm", "\\pm"],
    ["\\sqrt{x}", "\\sqrt"],
    ["\\sqrt[3]{x}", "\\sqrt[3]{}"],
    ["\\sqrt[n]{x}", "\\nthroot"],
    ["\\frac{x}{y}", "\\frac"],
    ["\\sum^{s}_{x}{d}", "\\sum"],
    ["\\prod^{s}_{x}{d}", "\\prod"],
    ["\\coprod^{s}_{x}{d}", "\\coprod"],
    ["\\int^{s}_{x}{d}", "\\int"],
    ["\\binom{n}{k}", "\\binom"]
  ];

  const toggleOperators = (event: { target: { checked: any; }; }) => {
    setOperators(CUSTOM_OPERATORS)
  };


  
  const options = { displayHistory, operators, displayDeleteButtonOnHistory };


    useEffect(() => {
        console.log('dd')
        if (!questionIsFetching) {
            setQuestionsArray([ ])
            // prefer full raw objects (response under data.full) when available
            const sourceArr: any[] = (questionsData && (questionsData as any).full) ? (questionsData as any).full : (questionsData.questions || []);
            sourceArr.forEach((element: any, key: number) => {
                const choiceA = element.choice_a ?? element.option1 ?? element.choiceA ?? '';
                const choiceB = element.choice_b ?? element.option2 ?? element.choiceB ?? '';
                const choiceC = element.choice_c ?? element.option3 ?? element.choiceC ?? '';
                const choiceD = element.choice_d ?? element.option4 ?? element.choiceD ?? '';
                const ansVal = element.ans ?? element.correct_ans ?? element.ansVal ?? '';

                setQuestionsArray(prevArray => [...prevArray,
                <div key={key} className={`container mx-auto px-10 py-4 mb-4 border rounded-lg transition-colors ${selectedQuestionIds.includes(element.id) ? 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800' : 'border-transparent'}`}>
                    <div className='pb-3 flex justify-between items-center'>
                        <div className="flex items-center gap-3">
                            <input
                                type="checkbox"
                                checked={selectedQuestionIds.includes(element.id)}
                                onChange={() => toggleQuestionSelection(element.id)}
                                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="bg-blue-200 text-blue-950 text-xs font-medium px-2.5 py-0.5 rounded-lg dark:bg-purple-900 dark:text-purple-300">Question <span className="font-bold">{key + 1} / {questionsData.questions.length}</span></span>
                        </div>
                        {element.shared_note_details && (
                            <div className="flex items-center gap-2">
                                <Badge color="info" className="cursor-help" title={element.shared_note_details.content}>
                                    Shared Note: {element.shared_note_details.title}
                                </Badge>
                                <button
                                    onClick={() => openEditSharedNote(element.shared_note_details)}
                                    className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                                    title="Edit Shared Note"
                                >
                                    <FaEdit size={14} />
                                </button>
                            </div>
                        )}
                    </div>
                    <div dangerouslySetInnerHTML={{ __html: renderLatexInHtml(element.ques || '') }} />
                    <br />
                    {/* Render question image if present (pending preview from upload or saved image URL) */}
                    {((element as any).pendingImagePreview) ? (
                        <div className="mb-3"><img src={String((element as any).pendingImagePreview)} alt={`preview-${key}`} className="max-h-48 mb-2" /></div>
                    ) : (element.image ? (
                        <div className="mb-3"><img src={String(element.image)} alt={`question-${key}`} className="max-h-48 mb-2" /></div>
                    ) : null)}
                    <div className="flex flex-row w-full gap-5">
                        <div className="basis-1/2 font-medium me-2 px-2.5 py-0.5 rounded bg-gray text-gray-800 dark:bg-gray dark:text-gray-300">
                            <div className='grid grid-cols-12'>
                                <span className="col-span-1 font-bold text-md justify-center items-center">A.</span>
                                <div className='col-span-11' dangerouslySetInnerHTML={{ __html: renderLatexInHtml(choiceA || '') }} />
                            </div>

                        </div>
                        <div className="basis-1/2 font-medium me-2 px-2.5 py-0.5 rounded bg-gray text-gray-800 dark:bg-gray dark:text-gray-300">
                            <div className='grid grid-cols-12'>
                                <span className="col-span-1 font-bold text-md">B.</span>
                                <div className='col-span-11' dangerouslySetInnerHTML={{ __html: renderLatexInHtml(choiceB || '') }} />
                            </div>
                        </div>
                    </div>
                    <br />
                    <div className="flex flex-row w-full gap-5">
                        <div className="basis-1/2 font-medium me-2 px-2.5 py-0.5 rounded bg-gray text-gray-800 dark:bg-gray dark:text-gray-300">
                            <div className='grid grid-cols-12'>
                                <span className="col-span-1 font-bold text-md">C.</span>
                                <div className='col-span-11' dangerouslySetInnerHTML={{ __html: renderLatexInHtml(choiceC || '') }} />
                            </div>
                        </div>
                        <div className="basis-1/2 font-medium me-2 px-2.5 py-0.5 rounded bg-gray text-gray-800 dark:bg-gray dark:text-gray-300">
                            <div className='grid grid-cols-12'>
                                <span className="col-span-1 font-bold text-md">D.</span>
                                <div className='col-span-11' dangerouslySetInnerHTML={{ __html: renderLatexInHtml(choiceD || '') }} />
                            </div>
                        </div>
                    </div>
                    <br />

                    <div className="grid grid-cols-2 gap-6">
                        <div>
                            {chapterEdit && !chapterListIsFetching ?
                                <>
                                    <div className="mb-2 block">
                                        <Label htmlFor="countries" value="Select Chapter" />
                                    </div>
                                    {
                                        element.chapter_name ?
                                            <Select id={`select-chapter-${element.id}`} title="Select Chapter" defaultValue={element.chapter_id?.toString()} onChange={(e) => changeChapterOnUpdate(e.target.value, element.id)}>
                                                <option value='-1'>Not Selected</option>
                                                {getFilteredChapters().map((item: any, index: number) => (
                                                    <option key={index} value={item.id}>{item.unit ? `Unit ${item.unit}: ` : ''}{item.name}{item.grade_name ? ` - ${item.grade_name}` : ''}</option>
                                                ))}

                                            </Select>
                                            :
                                            <Select id={`select-chapter-${element.id}`} title="Select Chapter" defaultValue='-1' onChange={(e) => changeChapterOnUpdate(e.target.value, element.id)}>
                                                <option value='-1'>Not Selected</option>
                                                {getFilteredChapters().map((item: any, index: number) => (
                                                    <option key={index} value={item.id}>{item.unit ? `Unit ${item.unit}: ` : ''}{item.name}{item.grade_name ? ` - ${item.grade_name}` : ''}</option>
                                                ))}

                                            </Select>
                                    }

                                </>
                                : null
                            }
                            {
                                Object.keys(questionChapterChanged).includes(`${element.id}`) ?
                                    <span className="text-red-700">Unsaved change</span>
                                    : null
                            }


                        </div>
                        <div>
                                    <div className='grid grid-cols-12 pb-3'>
                                <span className="col-span-12 font-medium text-lg"><Badge className="pl-5" icon={FaCheck} size="sm" > Answer: {ansVal}</Badge></span>
                            </div>
                            <div className='grid grid-cols-12'>
                                {
                                    // If question uses ChapterOption/SubChapterOption mode, prefer showing
                                    // "chapter option > subchapter option". Fall back to legacy chapter_name.
                                    (() => {
                                        try {
                                            const isOpt = !!element.is_chapteroption;
                                            const chapOpt = element.chapter_option_name ?? element.chapter_option ?? null;
                                            const subOpt = element.subchapter_option_name ?? element.subchapter_option ?? null;

                                            if (isOpt) {
                                                const left = chapOpt || element.chapter_name || null;
                                                const right = subOpt || element.subchapter_name || null;
                                                if (left) {
                                                    return (
                                                        <span className="col-span-12 font-medium text-lg"><Badge className="pl-5" icon={FaCheck} size="sm"> Chapter: {left}{right ? ` > ${right}` : ''}</Badge></span>
                                                    )
                                                }
                                                // is_chapteroption true but no names available
                                                return (<span className="col-span-12 font-medium text-lg"><Badge color="failure" className="pl-5" icon={FaTimes} size="sm" >Chapter: Not Assigned</Badge></span>)
                                            }
                                            // legacy path
                                            if (element.chapter_name) {
                                                return (<span className="col-span-12 font-medium text-lg"><Badge className="pl-5" icon={FaCheck} size="sm" > Chapter: {element.chapter_name}</Badge></span>)
                                            }
                                            return (<span className="col-span-12 font-medium text-lg"><Badge color="failure" className="pl-5" icon={FaTimes} size="sm" >Chapter: Not Assigned</Badge></span>)
                                        } catch (e) {
                                            return (<span className="col-span-12 font-medium text-lg"><Badge color="failure" className="pl-5" icon={FaTimes} size="sm" >Chapter: Not Assigned</Badge></span>)
                                        }
                                    })()
                                }
                            </div>
                        </div>
                    </div>
                    <br />
                    {/* Show explanation and reference if present */}
                    {element.explanation ? (
                        <div className="mb-4 w-full rounded-lg border p-4 shadow-sm bg-white dark:bg-[#0b1220] border-gray-100 dark:border-neutral-800">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                    <FaInfoCircle className="text-blue-600 dark:text-blue-400" />
                                    <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Explanation</h4>
                                </div>
                                <span className="text-xs text-gray-500 dark:text-gray-400">Rendered</span>
                            </div>
                            <div className="mt-2 text-sm leading-relaxed text-gray-700 dark:text-gray-200" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(element.explanation) }} />
                        </div>
                    ) : null}

                    {element.reference ? (
                        <div className="mb-4 w-full rounded-lg border p-4 shadow-sm bg-white dark:bg-[#071025] border-gray-100 dark:border-neutral-800">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                    <FaQuestion className="text-green-600 dark:text-green-400" />
                                    <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Reference</h4>
                                </div>
                                <span className="text-xs text-gray-500 dark:text-gray-400">Sources</span>
                            </div>
                            <div className="mt-2 text-sm text-gray-700 dark:text-gray-200">
                                {/* Split references by '||' and render as a list */}
                                {String(element.reference).split('||').map((ref: string, i: number) => {
                                    const s = ref.trim();
                                    if (!s) return null;
                                    return (
                                        <div key={i} className="mb-2">
                                            <div className="text-xs text-gray-500 dark:text-gray-400">Source {i + 1}</div>
                                            <div className="prose prose-sm max-w-none text-gray-800 dark:text-gray-100" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(s) }} />
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    ) : null}

                    <div className="flex items-center justify-center space-x-3.5">
                        <button title="Edit button" onClick={() => questionEdit(key)} type="button" className="text-blue-700 border border-blue-700 hover:bg-blue-700 hover:text-white focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-full text-sm p-2.5 text-center inline-flex items-center dark:border-blue-500 dark:text-blue-500 dark:hover:text-white dark:focus:ring-blue-800 dark:hover:bg-blue-500">
                            <svg
                                className="fill-current"
                                width="18"
                                height="18"
                                viewBox="0 0 18 18"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M8.99981 14.8219C3.43106 14.8219 0.674805 9.50624 0.562305 9.28124C0.47793 9.11249 0.47793 8.88749 0.562305 8.71874C0.674805 8.49374 3.43106 3.20624 8.99981 3.20624C14.5686 3.20624 17.3248 8.49374 17.4373 8.71874C17.5217 8.88749 17.5217 9.11249 17.4373 9.28124C17.3248 9.50624 14.5686 14.8219 8.99981 14.8219ZM1.85605 8.99999C2.4748 10.0406 4.89356 13.5562 8.99981 13.5562C13.1061 13.5562 15.5248 10.0406 16.1436 8.99999C15.5248 7.95936 13.1061 4.44374 8.99981 4.44374C4.89356 4.44374 2.4748 7.95936 1.85605 8.99999Z"
                                    fill=""
                                />
                                <path
                                    d="M9 11.3906C7.67812 11.3906 6.60938 10.3219 6.60938 9C6.60938 7.67813 7.67812 6.60938 9 6.60938C10.3219 6.60938 11.3906 7.67813 11.3906 9C11.3906 10.3219 10.3219 11.3906 9 11.3906ZM9 7.875C8.38125 7.875 7.875 8.38125 7.875 9C7.875 9.61875 8.38125 10.125 9 10.125C9.61875 10.125 10.125 9.61875 10.125 9C10.125 8.38125 9.61875 7.875 9 7.875Z"
                                    fill=""
                                />
                            </svg>
                        </button>
                        <button title="Delete button" onClick={() => handleDeleteQuestion(element.id)} type="button" className="text-blue-700 border border-blue-700 hover:bg-blue-700 hover:text-white focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-full text-sm p-2.5 text-center inline-flex items-center dark:border-blue-500 dark:text-blue-500 dark:hover:text-white dark:focus:ring-blue-800 dark:hover:bg-blue-500">
                            <svg
                                className="fill-current"
                                width="18"
                                height="18"
                                viewBox="0 0 18 18"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M13.7535 2.47502H11.5879V1.9969C11.5879 1.15315 10.9129 0.478149 10.0691 0.478149H7.90352C7.05977 0.478149 6.38477 1.15315 6.38477 1.9969V2.47502H4.21914C3.40352 2.47502 2.72852 3.15002 2.72852 3.96565V4.8094C2.72852 5.42815 3.09414 5.9344 3.62852 6.1594L4.07852 15.4688C4.13477 16.6219 5.09102 17.5219 6.24414 17.5219H11.7004C12.8535 17.5219 13.8098 16.6219 13.866 15.4688L14.3441 6.13127C14.8785 5.90627 15.2441 5.3719 15.2441 4.78127V3.93752C15.2441 3.15002 14.5691 2.47502 13.7535 2.47502ZM7.67852 1.9969C7.67852 1.85627 7.79102 1.74377 7.93164 1.74377H10.0973C10.2379 1.74377 10.3504 1.85627 10.3504 1.9969V2.47502H7.70664V1.9969H7.67852ZM4.02227 3.96565C4.02227 3.85315 4.10664 3.74065 4.24727 3.74065H13.7535C13.866 3.74065 13.9785 3.82502 13.9785 3.96565V4.8094C13.9785 4.9219 13.8941 5.0344 13.7535 5.0344H4.24727C4.13477 5.0344 4.02227 4.95002 4.02227 4.8094V3.96565ZM11.7285 16.2563H6.27227C5.79414 16.2563 5.40039 15.8906 5.37227 15.3844L4.95039 6.2719H13.0785L12.6566 15.3844C12.6004 15.8625 12.2066 16.2563 11.7285 16.2563Z"
                                    fill=""
                                />
                                <path
                                    d="M9.00039 9.11255C8.66289 9.11255 8.35352 9.3938 8.35352 9.75942V13.3313C8.35352 13.6688 8.63477 13.9782 9.00039 13.9782C9.33789 13.9782 9.64727 13.6969 9.64727 13.3313V9.75942C9.64727 9.3938 9.33789 9.11255 9.00039 9.11255Z"
                                    fill=""
                                />
                                <path
                                    d="M11.2502 9.67504C10.8846 9.64692 10.6033 9.90004 10.5752 10.2657L10.4064 12.7407C10.3783 13.0782 10.6314 13.3875 10.9971 13.4157C11.0252 13.4157 11.0252 13.4157 11.0533 13.4157C11.3908 13.4157 11.6721 13.1625 11.6721 12.825L11.8408 10.35C11.8408 9.98442 11.5877 9.70317 11.2502 9.67504Z"
                                    fill=""
                                />
                                <path
                                    d="M6.72245 9.67504C6.38495 9.70317 6.1037 10.0125 6.13182 10.35L6.3287 12.825C6.35683 13.1625 6.63808 13.4157 6.94745 13.4157C6.97558 13.4157 6.97558 13.4157 7.0037 13.4157C7.3412 13.3875 7.62245 13.0782 7.59433 12.7407L7.39745 10.2657C7.39745 9.90004 7.08808 9.64692 6.72245 9.67504Z"
                                    fill=""
                                />
                            </svg>
                        </button>
                    </div>
                    <br /><br />
                </div>])
            })
            console.log(questionsArray)
        }
    }, [questionIsFetching, chapterEdit, chapterListIsFetching, questionChapterChanged, selectedQuestionIds])
    
    // After questions render, run KaTeX auto-render to catch any inline/display math we didn't replace
    useEffect(() => {
        try {
            if (containerRef.current) {
                renderMathInElement(containerRef.current, {
                    delimiters: [
                        { left: '$$', right: '$$', display: true },
                        { left: '$', right: '$', display: false },
                        { left: '\\(', right: '\\)', display: false },
                        { left: '\\[', right: '\\]', display: true }
                    ],
                    throwOnError: false,
                })
            }
        } catch (e) {
            console.warn('KaTeX auto-render failed', e)
        }
    }, [questionsArray])
    console.log(questionsArray)

    return (
        <>
            <LaTeXBuilder show={showMathBuilder} onClose={() => setShowMathBuilder(false)} initialValue={''} onInsert={(tex) => {
                try {
                    const cur = String(selectedQuestion?.ques || '')
                    // append as inline math by default
                    const appended = cur + (cur && !cur.endsWith(' ') ? ' ' : '') + `$${tex}$`
                    questionEditChange(appended, 'ques')
                } catch (e) {
                    // fallback
                    questionEditChange((selectedQuestion?.ques || '') + `$${tex}$`, 'ques')
                }
                setShowMathBuilder(false)
            }} />
            <Modal size="4xl" className='fixed left-0 top-0 z-999999 flex h-full min-h-screen w-full items-center justify-center bg-black/50 px-4 py-5' show={openModal} onClose={() => setOpenModal(false)}>
                <Modal.Header>Terms of Service</Modal.Header>
                <Modal.Body>
                    <div className="space-y-6 h-100">
                        <span className="font-medium me-2 px-2.5 py-0.5 rounded bg-gray text-gray-800 dark:bg-gray dark:text-gray-300">
                            Question
                        </span>
                        
                        <RichSlateEditor initialValue={selectedQuestion?.ques} onChange={(html) => { questionEditChange(html, 'ques') }} />
                            
                        
                        <br />
                        <span className="basis-1/2 font-medium me-2 px-2.5 py-0.5 rounded bg-gray text-gray-800 dark:bg-gray dark:text-gray-300">
                            Choice A
                        </span>
                        <RichSlateEditor initialValue={selectedQuestion?.choice_a} onChange={(html) => { questionEditChange(html, 'choice_a') }} />
                        <br />
                        <span className="basis-1/2 font-medium me-2 px-2.5 py-0.5 rounded bg-gray text-gray-800 dark:bg-gray dark:text-gray-300">
                            Choice B
                        </span>
                        <RichSlateEditor initialValue={selectedQuestion?.choice_b} onChange={(html) => { questionEditChange(html, 'choice_b') }} />
                        <br />
                        <span className="basis-1/2 font-medium me-2 px-2.5 py-0.5 rounded bg-gray text-gray-800 dark:bg-gray dark:text-gray-300">
                            Choice C
                        </span>
                        <RichSlateEditor initialValue={selectedQuestion?.choice_c} onChange={(html) => { questionEditChange(html, 'choice_c') }} />
                        
                        <br />
                        <span className="basis-1/2 font-medium me-2 px-2.5 py-0.5 rounded bg-gray text-gray-800 dark:bg-gray dark:text-gray-300">
                            Choice D
                        </span>
                        <RichSlateEditor initialValue={selectedQuestion?.choice_d} onChange={(html) => { questionEditChange(html, 'choice_d') }} />
                        
                        <br />
                        <span className="basis-1/2 font-medium me-2 px-2.5 py-0.5 rounded bg-gray text-gray-800 dark:bg-gray dark:text-gray-300">
                            Answer
                        </span>
                        <div className='columns-3'>
                            <div></div>
                            <select
                                title='Answer choice'
                                id='answer_choice_select'
                                value={selectedOption}
                                onChange={(e) => {
                                    questionEditChange(e.target.value, 'ans')
                                    setSelectedOption(e.target.value);
                                    changeTextColor();
                                }}
                                className={`relative z-20 w-full appearance-none rounded border border-stroke bg-transparent py-3 px-12 outline-none transition focus:border-primary active:border-primary dark:border-form-strokedark dark:bg-form-input ${isOptionSelected ? 'text-black dark:text-white' : ''
                                    }`}
                            >
                                <option value="A" className="text-body dark:text-bodydark">
                                    Choice A
                                </option>
                                <option value="B" className="text-body dark:text-bodydark">
                                    Choice B
                                </option>
                                <option value="C" className="text-body dark:text-bodydark">
                                    Choice C
                                </option>
                                <option value="D" className="text-body dark:text-bodydark">
                                    Choice D
                                </option>
                            </select>
                        </div>
                        <div className="mt-4">
                            <h4 className="text-sm font-semibold mb-2">Explanation (optional)</h4>
                            <RichSlateEditor initialValue={selectedQuestion?.explanation} onChange={(html) => questionEditChange(html, 'explanation')} />
                        </div>

                        <div className="mt-4">
                            <h4 className="text-sm font-semibold mb-2">Reference (optional)</h4>
                            <textarea
                                value={selectedQuestion?.reference || ''}
                                onChange={(e) => questionEditChange(e.target.value, 'reference')}
                                rows={6}
                                className="w-full rounded border p-3 min-h-[140px] resize-vertical"
                                placeholder="Add references or sources, separate multiple with ||"
                            />
                        </div>

                        <br /><br /><br /><br /><br /><br />



                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={(e) => saveQuestionEdit(e)}>Save</Button>
                    <Button color="gray" onClick={() => setOpenModal(false)}>
                        Cancel
                    </Button>
                </Modal.Footer>
            </Modal>
            <div className="grid grid-cols-12 gap-5">
                <div className="col-span-9">
                    <div className="w-full bg-white px-5 pt-6 pb-2.5 shadow-default dark:border-strokedark dark:bg-boxdark sm:px-7.5 xl:pb-1">
                        <div className="max-w-full overflow-x-auto">
                            <div className="flex items-center justify-between">
                                <h4 className="mb-6 text-xl font-semibold text-black dark:text-white">
                                    Exam Info
                                </h4>
                                <Button size="xs" onClick={openEditInfo}>Edit Info</Button>
                            </div>
                            <Modal show={editInfoOpen} size="md" onClose={() => setEditInfoOpen(false)} popup>
                                <Modal.Header />
                                <Modal.Body>
                                    <div className="space-y-4">
                                        <h3 className="text-lg font-semibold">Edit Exam Info</h3>
                                        <div>
                                            <Label htmlFor="examName" value="Exam Name" />
                                            <input id="examName" title="Exam Name" placeholder="Exam Name" type="text" value={editName} onChange={(e) => setEditName(e.target.value)} className="mt-1 w-full rounded border border-stroke p-2 dark:border-form-strokedark dark:bg-form-input" />
                                        </div>
                                        <div>
                                            <Label htmlFor="examTime" value="Time (minutes)" />
                                            <input id="examTime" title="Time (minutes)" placeholder="Time in minutes" type="number" min={0} value={editMinutes} onChange={(e) => setEditMinutes(e.target.value)} className="mt-1 w-full rounded border border-stroke p-2 dark:border-form-strokedark dark:bg-form-input" />
                                        </div>
                                        <div className="flex justify-end gap-2">
                                            <Button onClick={saveEditInfo} isProcessing={isEditing}>Save</Button>
                                            <Button color="gray" onClick={() => setEditInfoOpen(false)}>Cancel</Button>
                                        </div>
                                    </div>
                                </Modal.Body>
                            </Modal>


                            {
                                examError != null ? <p>Error</p>
                                    : examStatus == 'success' ?
                                        <div className="flex flex-row justify-center gap-5">
                                            <div className="basis-1/2">
                                                <div className="flex flex-row">
                                                    <span className="basis-1/2">Exam Name:</span>
                                                    <span className="basis-1/2 bg-gray text-gray-800  font-medium me-2 px-2.5 py-0.5 rounded dark:bg-gray dark:text-gray-300">{examData.exam['name']}</span>
                                                </div>
                                                <br />
                                                    {
                                                        // For university/college roles show field of study / academic year / courses
                                                            (() => {
                                                                // Prefer exam's institution type when available; fall back to user's role.
                                                                // Also treat as university if university-specific fields are present.
                                                                const exam = (examData as any)?.exam || {};

                                                                const hasUniversityFields = !!(
                                                                    exam?.course || exam?.academic_year || exam?.field_of_study ||
                                                                    (Array.isArray(exam?.courses) && exam.courses.length > 0)
                                                                );

                                                                const instType = (exam?.institution && (exam.institution.inst_type ?? exam.institution_type)) ?? (exam.inst_type ?? '');
                                                                const isUniversityByInst = typeof instType === 'string' && /college|university/i.test(String(instType));
                                                                const isUniversityByRole = (role || '').toString().toLowerCase() === 'university_admin';
                                                                const isUniversity = hasUniversityFields || isUniversityByInst || isUniversityByRole;

                                                                const getName = (v: any): string => {
                                                                    if (!v && v !== 0) return '';
                                                                    if (typeof v === 'string') return v;
                                                                    if (typeof v === 'number') return String(v);
                                                                    if (Array.isArray(v)) return v.map(it => getName(it?.name ?? it)).filter(Boolean).join(', ');
                                                                    if (typeof v === 'object') return (v.name ?? v.title ?? '') as string;
                                                                    return String(v);
                                                                }

                                                                if (isUniversity) {
                                                                    // For university exams we only show the Courses field (hide field of study & academic year)
                                                                    const courses = exam.courses ?? exam['courses'] ?? exam.course ?? exam['course'] ?? null;
                                                                    return (
                                                                        <>
                                                                            <div className="flex flex-row">
                                                                                <span className="basis-1/2">Courses:</span>
                                                                                <span className="basis-1/2 bg-gray text-gray-800  font-medium me-2 px-2.5 py-0.5 rounded dark:bg-gray dark:text-gray-300">{getName(courses)}</span>
                                                                            </div>
                                                                        </>
                                                                    )
                                                                }

                                                                // default school view
                                                                return (
                                                                    <>
                                                                        <div className="flex flex-row">
                                                                            <span className="basis-1/2">Subject:</span>
                                                                            <span className="basis-1/2 bg-gray text-gray-800  font-medium me-2 px-2.5 py-0.5 rounded dark:bg-gray dark:text-gray-300">{examData.exam['subject']}</span>
                                                                        </div>
                                                                        <br />
                                                                        <div className="flex flex-row">
                                                                            <span className="basis-1/2">Grade:</span>
                                                                            <span className="basis-1/2 bg-gray text-gray-800  font-medium me-2 px-2.5 py-0.5 rounded dark:bg-gray dark:text-gray-300">{examData.exam['grade']}</span>
                                                                        </div>
                                                                    </>
                                                                )
                                                            })()
                                                    }
                                            </div>
                                            <div className="basis-1/2">
                                                <div className="flex flex-row">
                                                    <span className="basis-1/2">Time Given:</span>
                                                    <span className="basis-1/2 bg-gray text-gray-800  font-medium me-2 px-2.5 py-0.5 rounded dark:bg-gray dark:text-gray-300">{examData.exam['time']}</span>
                                                </div>
                                                <br />
                                                <div className="flex flex-row">
                                                    <span className="basis-1/2">
                                                        Status:
                                                    </span>
                                                    {isExamStatusChanging || isStatFetching ?
                                                        <Spinner aria-label="Default status example" />
                                                        : <label
                                                            htmlFor="toggle3"
                                                            className="flex cursor-pointer select-none items-center"
                                                        >
                                                            <div className="relative">
                                                                <input
                                                                    title="status"
                                                                    type="checkbox"
                                                                    id="toggle3"
                                                                    className="sr-only"
                                                                    onChange={changingExamStatus}
                                                                />
                                                                <div className="block h-8 w-14 rounded-full bg-meta-9 dark:bg-[#5A616B]"></div>
                                                                <div
                                                                    className={`dot absolute left-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-white transition ${statusSwitch && '!right-1 !translate-x-full !bg-primary dark:!bg-white'
                                                                        }`}
                                                                >
                                                                    <span className={`hidden ${statusSwitch && '!block'}`}>
                                                                        <svg
                                                                            className="fill-white dark:fill-black"
                                                                            width="11"
                                                                            height="8"
                                                                            viewBox="0 0 11 8"
                                                                            fill="none"
                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                        >
                                                                            <path
                                                                                d="M10.0915 0.951972L10.0867 0.946075L10.0813 0.940568C9.90076 0.753564 9.61034 0.753146 9.42927 0.939309L4.16201 6.22962L1.58507 3.63469C1.40401 3.44841 1.11351 3.44879 0.932892 3.63584C0.755703 3.81933 0.755703 4.10875 0.932892 4.29224L0.932878 4.29225L0.934851 4.29424L3.58046 6.95832C3.73676 7.11955 3.94983 7.2 4.1473 7.2C4.36196 7.2 4.55963 7.11773 4.71406 6.9584L10.0468 1.60234C10.2436 1.4199 10.2421 1.1339 10.0915 0.951972ZM4.2327 6.30081L4.2317 6.2998C4.23206 6.30015 4.23237 6.30049 4.23269 6.30082L4.2327 6.30081Z"
                                                                                fill=""
                                                                                stroke=""
                                                                                strokeWidth="0.4"
                                                                            ></path>
                                                                        </svg>
                                                                    </span>
                                                                    <span className={`${statusSwitch && 'hidden'}`}>
                                                                        <svg
                                                                            className="h-4 w-4 stroke-current"
                                                                            fill="none"
                                                                            viewBox="0 0 24 24"
                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                        >
                                                                            <path
                                                                                strokeLinecap="round"
                                                                                strokeLinejoin="round"
                                                                                strokeWidth="2"
                                                                                d="M6 18L18 6M6 6l12 12"
                                                                            ></path>
                                                                        </svg>
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </label>
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                        : <p>Loading ...</p>
                            }
                            <br /><br />
                        </div>

                    </div>
                    <br />
                    <div className="bg-white px-5 pt-6 pb-2.5 shadow-default dark:border-strokedark dark:bg-boxdark sm:px-7.5 xl:pb-1">

                        <div className="max-w-full overflow-x-auto">
                        
                            <div className="flex flex-wrap items-start justify-between gap-3 sm:flex-nowrap">

                                <div className="flex w-full max-w-45 ">
                                    <h4 className="mb-6 text-xl font-semibold text-black dark:text-white">
                                        Questions
                                    </h4>
                                </div>
                                <div className="flex w-full flex-wrap gap-3 sm:gap-5 justify-end">
                                    <Button size="sm" color="success" onClick={() => setAddQuestionsOpen(true)}>
                                        <FaPlus className="mr-2 h-4 w-4" /> Add Other Questions
                                    </Button>

                                    {selectedQuestionIds.length > 0 && (
                                        <Button size="sm" color="info" onClick={() => setSharedNoteModalOpen(true)}>
                                            Assign Shared Note ({selectedQuestionIds.length})
                                        </Button>
                                    )}

                                    <div className="flex min-w-49">
                                        <div className="pr-3 pt-1">
                                            <p className="font-semibold text-success">Chapter Edit Mode</p>
                                        </div>
                                        <span className="">
                                            {chapterListIsFetching ?
                                                <Spinner aria-label="Default status example" />
                                                : <label
                                                    htmlFor="chapterediting"
                                                    className="flex cursor-pointer select-none items-center"
                                                >
                                                    <div className="relative">
                                            <input
                                                title="chapter edit toggle"
                                                type="checkbox"
                                                id="chapterediting"
                                                className="sr-only"
                                                onChange={() => changeSetChapterEdit()}
                                            />
                                                        <div className="block h-8 w-14 rounded-full bg-meta-9 dark:bg-[#5A616B]"></div>
                                                        <div
                                                            className={`dot absolute left-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-white transition ${chapterEdit && '!right-1 !translate-x-full !bg-success dark:!bg-white'
                                                                }`}
                                                        >
                                                            <span className={`hidden ${chapterEdit && '!block'}`}>
                                                                <svg
                                                                    className="fill-white dark:fill-black"
                                                                    width="11"
                                                                    height="8"
                                                                    viewBox="0 0 11 8"
                                                                    fill="none"
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                >
                                                                    <path
                                                                        d="M10.0915 0.951972L10.0867 0.946075L10.0813 0.940568C9.90076 0.753564 9.61034 0.753146 9.42927 0.939309L4.16201 6.22962L1.58507 3.63469C1.40401 3.44841 1.11351 3.44879 0.932892 3.63584C0.755703 3.81933 0.755703 4.10875 0.932892 4.29224L0.932878 4.29225L0.934851 4.29424L3.58046 6.95832C3.73676 7.11955 3.94983 7.2 4.1473 7.2C4.36196 7.2 4.55963 7.11773 4.71406 6.9584L10.0468 1.60234C10.2436 1.4199 10.2421 1.1339 10.0915 0.951972ZM4.2327 6.30081L4.2317 6.2998C4.23206 6.30015 4.23237 6.30049 4.23269 6.30082L4.2327 6.30081Z"
                                                                        fill=""
                                                                        stroke=""
                                                                        strokeWidth="0.4"
                                                                    ></path>
                                                                </svg>
                                                            </span>
                                                            <span className={`${chapterEdit && 'hidden'}`}>
                                                                <svg
                                                                    className="h-4 w-4 stroke-current"
                                                                    fill="none"
                                                                    viewBox="0 0 24 24"
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                >
                                                                    <path
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                        strokeWidth="2"
                                                                        d="M6 18L18 6M6 6l12 12"
                                                                    ></path>
                                                                </svg>
                                                            </span>
                                                        </div>
                                                    </div>
                                                </label>
                                            }
                                        </span>
                                        
                                    </div>
                                </div>
                            </div>
                            <Tabs className="pl-1 border-b-gray border-b-2" aria-label="Default tabs" variant="default">
                                <Tabs.Item active title="Questions" icon={FaQuestion}>
                                    <div ref={containerRef}>{questionsArray}</div>
                                    {
                                        chapterEdit ?
                                            <div className="flex w-full justify-center">
                                                <div className="fixed bottom-0 p-2 w-50 h-24">
                                                    {Object.keys(questionChapterChanged).length > 0 ?
                                                        <Button size='lg' onClick={() => setOpenVerificationModal(true)} className="text-white shadow-2xl backdrop-blur-3xl rounded bg-gradient-to-r from-cyan-500 to-blue-500 z-50 absolute">SAVE CHANGES</Button>

                                                        :
                                                        <Button size='lg' className="text-white shadow-2xl backdrop-blur-3xl rounded bg-gradient-to-r from-cyan-500 to-blue-500 z-50 absolute" disabled>SAVE CHANGES</Button>
                                                    }
                                                    <Modal show={openVerificationModal} size="md" onClose={() => setOpenVerificationModal(false)} popup>
                                                        <Modal.Header />
                                                        <Modal.Body>
                                                            <div className="text-center">
                                                                <FaInfoCircle className="mx-auto mb-4 h-14 w-14 text-gray-400 dark:text-gray-200" />
                                                                <h3 className="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400">
                                                                    Are you sure you want to save these chapter changes?
                                                                </h3>
                                                                <div className="flex justify-center gap-4">
                                                                    <Button color="failure" onClick={(e) => changingChpater(e)}>
                                                                        Save changes
                                                                    </Button>
                                                                    <Button color="gray" onClick={() => setOpenVerificationModal(false)}>
                                                                        Cancel
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        </Modal.Body>
                                                    </Modal>
                                                </div>
                                            </div>
                                            : null
                                    }

                                    <Modal show={chapterGradeModalOpen} size="md" onClose={() => setChapterGradeModalOpen(false)} popup>
                                        <Modal.Header/>
                                        <Modal.Body>
                                            <div className="space-y-4">
                                                <h3 className="text-lg font-semibold">Select grades to include chapters from</h3>
                                                <div className="flex gap-2">
                                                    <Button size='xs' onClick={selectAllGrades}>Select All</Button>
                                                    <Button size='xs' color="gray" onClick={clearAllGrades}>Clear</Button>
                                                </div>
                                                <div className="max-h-60 overflow-y-auto border rounded p-3">
                                                    {gradeOptions.length === 0 ? (
                                                        <p className="text-sm text-gray-500">No grades found for this subject in your institution.</p>
                                                    ) : (
                                                        gradeOptions.map(opt => (
                                                            <label key={opt.id} className="flex items-center gap-2 py-1">
                                                                <input type="checkbox" checked={!!selectedGradeIds?.includes(opt.id)} onChange={() => toggleGrade(opt.id)} />
                                                                <span>{opt.name}</span>
                                                            </label>
                                                        ))
                                                    )}
                                                </div>
                                                <div className="flex justify-end gap-2">
                                                    <Button onClick={() => {
                                                        setAppliedGradeIds([...selectedGradeIds]);
                                                        setChapterGradeModalOpen(false);
                                                    }}>Apply</Button>
                                                    <Button color="gray" onClick={() => {
                                                        setSelectedGradeIds([...appliedGradeIds]);
                                                        setChapterGradeModalOpen(false);
                                                    }}>Cancel</Button>
                                                </div>
                                            </div>
                                        </Modal.Body>
                                    </Modal>
                                </Tabs.Item>
                                <Tabs.Item title="Statistics" icon={FaChartLine}>
                                    This is <span className="font-medium text-gray-800 dark:text-white">Dashboard tab's associated content</span>.
                                    Clicking another tab will toggle the visibility of this one for the next. The tab JavaScript swaps classes to
                                    control the content visibility and styling.
                                </Tabs.Item>

                            </Tabs>

                        </div>

                    </div >

                </div >
                <div className="col-span-3">
                    <div className=" bg-white px-5 pt-6 pb-2.5 shadow-default dark:border-strokedark dark:bg-boxdark sm:px-7.5 xl:pb-1"></div>
                </div>
            </div>
            <br /><br />

            <Modal show={sharedNoteModalOpen} size="2xl" onClose={() => setSharedNoteModalOpen(false)}>
                <Modal.Header>Assign Shared Note</Modal.Header>
                <Modal.Body>
                    <div className="space-y-4">
                        <p className="text-sm text-gray-500">
                            You are assigning a note to {selectedQuestionIds.length} selected questions.
                        </p>
                        
                        <div className="border-b pb-4 mb-4">
                            <Label htmlFor="existing_note" value="Select Existing Note" />
                            <Select 
                                id="existing_note" 
                                value={existingSharedNoteId} 
                                onChange={(e) => setExistingSharedNoteId(e.target.value)}
                            >
                                <option value="">-- Create New Note --</option>
                                {sharedNotesData?.map((note: any) => (
                                    <option key={note.id} value={note.id}>{note.title}</option>
                                ))}
                            </Select>
                        </div>

                        {!existingSharedNoteId && (
                            <div className="space-y-4">
                                <div>
                                    <Label htmlFor="note_title" value="Note Title" />
                                    <input
                                        id="note_title"
                                        title="Note Title"
                                        className="w-full rounded border border-stroke p-2 dark:border-form-strokedark dark:bg-form-input"
                                        value={newSharedNote.title}
                                        onChange={(e) => setNewSharedNote({ ...newSharedNote, title: e.target.value })}
                                        placeholder="e.g. Reading Passage 1"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="note_content" value="Note Content (Large text/HTML)" />
                                    <RichSlateEditor
                                        initialValue={newSharedNote.content}
                                        onChange={(html) => setNewSharedNote({ ...newSharedNote, content: html })}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button 
                        onClick={handleAssignSharedNote} 
                        disabled={!existingSharedNoteId && (!newSharedNote.title || !newSharedNote.content)}
                        isProcessing={createSharedNoteMut.isPending || assignSharedNoteMut.isPending}
                    >
                        Apply to Selected Questions
                    </Button>
                    <Button color="gray" onClick={() => setSharedNoteModalOpen(false)}>
                        Cancel
                    </Button>
                </Modal.Footer>
            </Modal>

            <AddOtherQuestionsDialog 
                show={addQuestionsOpen} 
                onClose={() => setAddQuestionsOpen(false)} 
                examId={examId!} 
                onQuestionsAdded={() => {
                    refetchQuestions();
                    setAddQuestionsOpen(false);
                }}
            />

            <Modal show={editSharedNoteModalOpen} size="2xl" onClose={() => setEditSharedNoteModalOpen(false)}>
                <Modal.Header>Edit Shared Note</Modal.Header>
                <Modal.Body>
                    {editingSharedNote && (
                        <div className="space-y-4">
                            <div>
                                <Label htmlFor="edit_note_title" value="Note Title" />
                                <input
                                    id="edit_note_title"
                                    title="Note Title"
                                    className="w-full rounded border border-stroke p-2 dark:border-form-strokedark dark:bg-form-input"
                                    value={editingSharedNote.title}
                                    onChange={(e) => setEditingSharedNote({ ...editingSharedNote, title: e.target.value })}
                                />
                            </div>
                            <div>
                                <Label htmlFor="edit_note_content" value="Note Content" />
                                <RichSlateEditor
                                    initialValue={editingSharedNote.content}
                                    onChange={(html) => setEditingSharedNote({ ...editingSharedNote, content: html })}
                                />
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button 
                        onClick={handleUpdateSharedNote} 
                        disabled={!editingSharedNote?.title || !editingSharedNote?.content}
                        isProcessing={updateSharedNoteMut.isPending}
                    >
                        Update Note
                    </Button>
                    <Button color="gray" onClick={() => setEditSharedNoteModalOpen(false)}>
                        Cancel
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}

export default ExamDetailComponent
